import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { InputDate, ThemedAsset, assetIds } from "nemuru-components";
import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { ICON_TITLE_HEIGHT } from "../../../../../config/config";
import { ConfigContext } from "../../../../../contexts/ConfigContext";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import { filterArrayOfObjectsByKeyAndValues } from "../../../../../utils/filterArrayOfObjectsByKeyAndValues";
import { useNationalities } from "../../../../../utils/hooks/useNationalities/useNationalities";
import {
  DNI_REGEX,
  NIE_REGEX,
  PASSPORT_REGEX,
  validateKeyboardInputDate,
} from "../../../../../utils/validations";
import IconTitle from "../../../../shared/components/IconTitle";
import InfoPanel from "../../../../shared/components/InfoPanel";
import MainButton from "../../../../shared/components/MainButton";
import Text from "../../../../shared/components/Text";
import {
  docIdTypes,
  documentTypes,
  infoDataDocumentType,
} from "../../shared/config";
import { SPANISH_NATIONALITY } from "../constants/SpanishNationality";

export default function KycManualDocIdInfo({ onSubmitForm, data }) {
  const {
    state: { documentType, participant },
    dispatch,
  } = useContext(ProcessContext);

  const {
    state: { dictionaryValues },
  } = useContext(ConfigContext);

  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down("xs"));

  const { nationalities } = useNationalities();

  const { register, getValues, handleSubmit, errors, control, watch } = useForm(
    {
      defaultValues: {
        documentNumber:
          participant?.onboardingDetails?.invitation?.document_number ??
          data?.documentNumber,
        documentType: data?.documentType || documentType || "",
        documentExpeditor:
          !data?.documentExpeditor || documentType === docIdTypes.PASSPORT
            ? null
            : data?.documentExpeditor
            ? data?.documentExpeditor
            : SPANISH_NATIONALITY,
        expiration: data?.expiration || null,
      },
    }
  );

  const selectedDocumentType = watch("documentType");

  const getRegexBySelectedDocumentExpeditor = () => {
    switch (selectedDocumentType) {
      case docIdTypes.DNI_ES:
        return DNI_REGEX;
      case docIdTypes.NIE_NON_UE:
      case docIdTypes.NIE_UE:
        return NIE_REGEX;
      case docIdTypes.PASSPORT:
        return PASSPORT_REGEX;
      default:
        return undefined;
    }
  };

  const REGEX = getRegexBySelectedDocumentExpeditor();

  const validateDocumentNumberInFormAndInvitation = (value) => {
    const invitationIdNumber =
      participant.onboardingDetails.invitation.document_number;
    const isDeclaredIdNumberAsInInvitation = value === invitationIdNumber;
    if (invitationIdNumber && !isDeclaredIdNumberAsInInvitation) {
      return <Text tId={"DOCUMENT_ID_NUMBER_DIFFERENT_THAN_INVITATION"} />;
    }
  };

  // const validatePassportNumber = (value) => {
  //   if (selectedDocumentType !== docIdTypes.PASSPORT) return;
  //   const nationalityAlpha2Value = nationalities.find(
  //     (nationality) => nationality.value === selectedDocumentExpeditor
  //   )?.alpha2;
  //   try {
  //     const isValid = isValidPassportNumber(value, nationalityAlpha2Value);
  //     if (!isValid) return <Text tId={"INVALID_DOCUMENT_NUMBER"} />;
  //   } catch (error) {
  //     if (error instanceof CountryCodeNotSupportedForPassportValidation) return;
  //     throw error;
  //   }
  // };

  const handleManualDocIdSubmitInfo = (values) => {
    onSubmitForm(values);
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: { documentType: values.documentType },
    });
  };

  const SELECT_DOCUMENT_TYPE_OPTIONS_PASSPORT =
    filterArrayOfObjectsByKeyAndValues(documentTypes, "id", [
      docIdTypes.PASSPORT,
    ]);

  const SELECT_DOCUMENT_TYPE_OPTIONS = filterArrayOfObjectsByKeyAndValues(
    documentTypes,
    "id",
    [docIdTypes.DNI_ES, docIdTypes.NIE_UE, docIdTypes.NIE_NON_UE]
  );

  return (
    <>
      <InfoPanel infoData={infoDataDocumentType} />
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_ID_FRONT}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId="ID" />}
          subtitle={<Text tId="DOCUMENT_TYPE" />}
        />
        <FormControl fullWidth>
          <InputLabel id="documentType">
            <Text tId="SELECT_YOUR_ID" />
          </InputLabel>
          <Controller
            name="documentType"
            control={control}
            rules={{
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
            }}
            render={(props) => (
              <>
                <Select
                  MenuProps={{
                    MenuListProps: {
                      "aria-label": "Select document type",
                    },
                  }}
                  onChange={(e) => props.onChange(e.target.value)}
                  value={props.value}
                  error={errors.documentType ? true : false}
                >
                  {documentType === docIdTypes.PASSPORT
                    ? SELECT_DOCUMENT_TYPE_OPTIONS_PASSPORT.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.display}
                        </MenuItem>
                      ))
                    : SELECT_DOCUMENT_TYPE_OPTIONS.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.display}
                        </MenuItem>
                      ))}
                </Select>
                <FormHelperText style={{ color: palette.error.main }}>
                  {errors.documentType?.message}
                </FormHelperText>
              </>
            )}
          />
        </FormControl>
        <TextField
          fullWidth
          placeholder={
            watch("documentType") === docIdTypes.DNI_ES
              ? "12345678E"
              : watch("documentType") === docIdTypes.PASSPORT
              ? "AA000000"
              : "Y1234567X"
          }
          label={<Text tId={"DOCUMENT_NUMBER"} />}
          type="text"
          id="documentNumber"
          name="documentNumber"
          error={errors.documentNumber ? true : false}
          helperText={errors.documentNumber?.message}
          inputProps={{
            max: {
              value: 9,
              message: <Text tId={"INVALID_DOCUMENT_NUMBER"} />,
            },
            ref: register({
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
              pattern: {
                value: REGEX,
                message: <Text tId={"INVALID_DOCUMENT_NUMBER"} />,
              },
              validate: {
                validate: (value) =>
                  validateDocumentNumberInFormAndInvitation(value),
                // validatePassport: (value) => validatePassportNumber(value),
              },
            }),
          }}
        />
        <InputDate
          allowKeyboardPicking={!isMobile}
          locale={dictionaryValues["DATE_LOCALE"]}
          emptyLabel={dictionaryValues["DATE_FORMAT_DAY_MONTH_YEAR"]}
          control={control}
          fullWidth
          variant="inline"
          openTo="year"
          autoOk
          views={["year", "month", "date"]}
          format="dd/MM/yyyy"
          label={<Text tId="EXPIRATION" />}
          id="expiration"
          name="expiration"
          error={errors.expiration ? true : false}
          helperText={errors.expiration?.message}
          disablePast={true}
          rules={{
            required: {
              value: true,
              message: <Text tId={"REQUIRED_FIELD"} />,
            },
            validate: {
              validate: (value) =>
                validateKeyboardInputDate({ value, disablePast: true }),
            },
          }}
        />
        <FormControl fullWidth>
          <InputLabel id="documentExpeditor">
            <Text tId="DOCUMENT_EXPEDITOR" />
          </InputLabel>
          <Controller
            name="documentExpeditor"
            control={control}
            rules={{
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
            }}
            render={(props) => (
              <>
                <Select
                  MenuProps={{
                    MenuListProps: {
                      "aria-label": "Select document expeditor",
                    },
                  }}
                  onChange={(e) => props.onChange(e.target.value)}
                  value={props.value}
                  error={errors.documentExpeditor ? true : false}
                >
                  {nationalities.map((type, index) => (
                    <MenuItem key={index} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText style={{ color: palette.error.main }}>
                  {errors.documentExpeditor?.message}
                </FormHelperText>
              </>
            )}
          />
        </FormControl>
      </Box>
      <Box>
        <MainButton
          onClick={handleSubmit(() => handleManualDocIdSubmitInfo(getValues()))}
        />
      </Box>
    </>
  );
}
